import {
  company_list_path,
  leads_companies_path,
  leads_company_lists_path,
} from "@/lib/routes"
import { getCsrfToken } from "@/lib/utils"
import { Button } from "@/react/ui/button"
import { Icon } from "@/react/ui/icon"
import { Input } from "@/react/ui/input"
import { Label } from "@/react/ui/label"
import { Radio } from "@/react/ui/radio"
import { RadioGroup, RadioGroupItem } from "@/react/ui/radio-group"
import { Select } from "@/react/ui/select"
import { useState } from "react"
import type { DiscoverDomain, DiscoverFilters } from "~/react/types/discover"
import { useCompanyLists } from "@/react/components/company-lists-context"

const t = (key: string, options?: Record<string, unknown>) =>
  window.i18n.t(key, options)

interface CreateCompanyListResponse {
  id: number
  name: string
}

export default function SaveCompanies({
  numberOfDomains,
  domains,
  filters,
  closeSidepanel,
}: {
  numberOfDomains: number
  domains: DiscoverDomain[]
  filters: DiscoverFilters
  closeSidepanel: () => void
}): React.ReactNode {
  const { companyLists, setCompanyLists } = useCompanyLists()
  const [isSaving, setIsSaving] = useState(false)
  const [typeOfList, setTypeOfList] = useState<"new" | "existing">(
    companyLists.length > 0 ? "existing" : "new",
  )
  const [listName, setListName] = useState<string>("")
  const firstFiveDomainNames = domains
    .slice(0, 5)
    .map((domain) => domain.companyName)
    .join(", ")
  const descriptionCopy =
    numberOfDomains > 5
      ? t("js.discover.save_companies.description.more", {
          companies: firstFiveDomainNames,
          count: numberOfDomains - 5,
        })
      : firstFiveDomainNames

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSaving(true)
    const formData = new FormData(e.currentTarget)
    const url = leads_company_lists_path({ format: "json" })

    fetch(url, {
      method: "POST",
      headers: {
        "X-CSRF-Token": getCsrfToken(),
      },
      body: formData,
    })
      .then((response) => {
        if (!response.ok) {
          App.displayFlash("error", t("js.discover.save_companies.flash.error"))
          return null
        }
        return response.json()
      })
      .then((data: CreateCompanyListResponse) => {
        if (!companyLists.find((list) => list.id === data.id)) {
          setCompanyLists((prev) => [...prev, { id: data.id, name: data.name }])
        }
        const url = data.id
          ? company_list_path({ id: data.id })
          : leads_companies_path()
        App.displayFlash(
          "success",
          t("js.discover.save_companies.flash.success_html", {
            count: numberOfDomains,
            name: data.name,
            url: url,
          }),
        )
        closeSidepanel()
      })
      .catch(() => {
        App.displayFlash("error", t("js.discover.save_companies.flash.error"))
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  const companyListOptions = [
    { value: 0, label: t("js.discover.save_companies.all_companies") },
    ...companyLists.map((list) => ({
      value: list.id,
      label: list.name,
    })),
  ]

  return (
    <>
      <header className="sidepanel__header mb-0">
        <h2 className="sidepanel__title">
          {t("js.discover.save_companies.title")}
        </h2>
      </header>

      <form className="sidepanel__body" onSubmit={handleSubmit}>
        <input
          type="hidden"
          name="lead_company_list[filter_params]"
          value={JSON.stringify(filters)}
        />
        <input
          type="hidden"
          name="lead_company_list[timestamp]"
          value={new Date().getTime()}
        />

        <div className="bic-selection">
          <div className="bic-selection__icon">
            <Icon icon="buildings" size="md" />
          </div>
          <h3 className="bic-selection__title">
            {t("js.discover.save_companies.companies_selected", {
              count: numberOfDomains,
            })}
          </h3>
          <p className="bic-selection__description">{descriptionCopy}</p>
          <input
            type="hidden"
            name="lead_company_list[domains]"
            value={domains.map((domain) => domain.value).join(",")}
          />
        </div>

        <fieldset className="h-fieldset">
          <legend className="h-legend">
            {t("js.discover.save_companies.legend", { count: numberOfDomains })}
          </legend>

          <RadioGroup className="mb-6">
            <RadioGroupItem>
              <Radio
                name="import_list_type"
                id="existing-list"
                value="existing"
                checked={typeOfList === "existing"}
                onChange={() => setTypeOfList("existing")}
              />
              <Label htmlFor="existing-list" className="mb-0">
                {t("js.discover.save_companies.list_type.existing")}
              </Label>
            </RadioGroupItem>
            <RadioGroupItem>
              <Radio
                name="import_list_type"
                id="new-list"
                value="new"
                checked={typeOfList === "new"}
                onChange={() => setTypeOfList("new")}
              />
              <Label htmlFor="new-list" className="mb-0">
                {t("js.discover.save_companies.list_type.new")}
              </Label>
            </RadioGroupItem>
          </RadioGroup>

          {typeOfList === "existing" && (
            <>
              <Label htmlFor="lead_company_list_leads_list_id">
                {t("js.discover.save_companies.select_list")}
              </Label>
              <Select
                id="lead_company_list_leads_list_id"
                name="lead_company_list[leads_list_id]"
                isClearable={false}
                isMulti={false}
                theme={(theme) => ({
                  ...theme,
                  spacing: {
                    controlHeight: 40,
                  },
                })}
                options={companyListOptions}
                defaultValue={companyListOptions[0].value}
                required={typeOfList === "existing"}
              />
            </>
          )}

          {typeOfList === "new" && (
            <>
              <Label htmlFor="lead_company_list_name">
                {t("js.discover.save_companies.create_new_list")}
              </Label>
              <Input
                type="text"
                id="lead_company_list_name"
                name="lead_company_list[name]"
                fullWidth
                value={listName}
                required={typeOfList === "new"}
                onChange={(e) => setListName(e.target.value)}
              />
            </>
          )}
        </fieldset>

        <footer className="sidepanel__footer">
          <Button variant="primary" type="submit" disabled={isSaving}>
            {t("js.discover.save_companies.save_to_leads")}
          </Button>
          <Button variant="ghost" type="button" onClick={closeSidepanel}>
            {t("js.discover.save_companies.cancel")}
          </Button>
        </footer>
      </form>
    </>
  )
}
